/*
 * Gift promo in the cart drawer (snippets/gift-promo.liquid).
 * - Pick / swap: there is only ever ONE gift line; picking another one
 *   removes the old line first, then adds the new one with `_gift`.
 * - Orphan: if the paid products are removed, the leftover gift line is
 *   removed too (otherwise it would be charged in full).
 * - Checkout nudge: pressing "checkout" without a gift highlights the card
 *   once; "continue without a gift" lets her through.
 * - Toast: after the first add to cart of the session, a small note points
 *   to the gift picker (the drawer no longer opens by itself).
 * The free price itself comes from the automatic discount in the admin.
 */
(() => {
  if (customElements.get('gift-promo')) return;

  const SKIP_KEY = 'o8-gift-skip';
  const TOAST_KEY = 'o8-gift-toast';
  const store = (key, value) => {
    try {
      if (value === undefined) return sessionStorage.getItem(key);
      sessionStorage.setItem(key, value);
    } catch (e) {
      return null;
    }
  };

  const miniCart = () => document.querySelector('mini-cart');
  const sections = () => {
    const cart = miniCart();
    return cart ? cart.getSectionsToRender().map((section) => section.section) : [];
  };
  const post = (url, payload) =>
    fetch(url, { ...fetchConfig('javascript'), body: JSON.stringify(payload) }).then((r) => r.json());
  const render = (state) => {
    const cart = miniCart();
    if (cart && state && state.sections) cart.renderContents(state);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: state } }));
  };

  class GiftPromo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.orphanKey) {
        this.removeOrphan();
        return;
      }
      this.addEventListener('click', this.onClick.bind(this));
    }

    removeOrphan() {
      post(theme.routes.cart_change_url, {
        id: this.dataset.orphanKey,
        quantity: 0,
        sections: sections(),
        sections_url: window.location.pathname
      })
        .then(render)
        .catch((e) => console.error(e));
    }

    onClick(event) {
      const pick = event.target.closest('[data-gift-pick]');
      if (pick) return this.choose(pick);
      if (event.target.closest('[data-gift-open]')) return this.openChooser();
      if (event.target.closest('[data-gift-skip]')) return this.skip();
    }

    openChooser() {
      const chooser = this.querySelector('.gift-promo__chooser');
      const done = this.querySelector('.gift-promo__done');
      if (chooser) chooser.hidden = false;
      if (done) done.hidden = true;
    }

    async choose(button) {
      if (this.classList.contains('is-busy')) return;
      this.classList.add('is-busy');
      button.classList.add('loading');
      try {
        if (this.dataset.giftKey) {
          await post(theme.routes.cart_change_url, { id: this.dataset.giftKey, quantity: 0 });
        }
        const state = await post(theme.routes.cart_add_url, {
          items: [{ id: Number(button.dataset.giftPick), quantity: 1, properties: { _gift: 'election-2026' } }],
          sections: sections(),
          sections_url: window.location.pathname
        });
        if (state.status) throw new Error(state.description || 'add failed');
        render(state);
      } catch (e) {
        console.error(e);
        this.classList.remove('is-busy');
        button.classList.remove('loading');
      }
    }

    // First "checkout" press without a gift: point at the card instead.
    nudge() {
      this.classList.add('is-alert');
      const title = this.querySelector('[data-gift-title]');
      const sub = this.querySelector('[data-gift-sub]');
      if (title) title.textContent = this.dataset.alertTitle;
      if (sub) sub.textContent = this.dataset.alertSub;
      const skip = this.querySelector('[data-gift-skip]');
      if (skip) skip.hidden = false;
      this.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    skip() {
      store(SKIP_KEY, '1');
      const checkout = document.querySelector('#mini-cart [name="checkout"]') || document.querySelector('[name="checkout"]');
      if (checkout) checkout.click();
    }
  }

  customElements.define('gift-promo', GiftPromo);

  // Checkout without a gift → nudge once (capture phase, before the form submits).
  document.addEventListener(
    'click',
    (event) => {
      const checkout = event.target.closest('[name="checkout"]');
      // The terms checkbox gets first say; one message at a time.
      if (!checkout || event.defaultPrevented || store(SKIP_KEY) === '1') return;
      const promo = document.querySelector('gift-promo.gift-promo:not(.is-chosen)');
      if (!promo || promo.classList.contains('is-alert')) return;
      event.preventDefault();
      event.stopPropagation();
      promo.nudge();
    },
    true
  );

  // Toast after the first add to cart of the session.
  const showToast = () => {
    if (!window.theme || !theme.giftPromo || store(TOAST_KEY) === '1') return;
    store(TOAST_KEY, '1');
    const toast = document.createElement('div');
    toast.className = 'gift-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML =
      '<svg class="gift-toast__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '<span class="gift-toast__text"><strong></strong><span></span></span>' +
      '<button type="button" class="gift-toast__go"></button>';
    toast.querySelector('strong').textContent = theme.giftPromo.toastTitle;
    toast.querySelector('.gift-toast__text span').textContent = theme.giftPromo.toastText;
    const go = toast.querySelector('.gift-toast__go');
    go.textContent = theme.giftPromo.toastCta;
    go.addEventListener('click', () => {
      toast.remove();
      const cart = miniCart();
      if (cart) cart.open();
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 400);
    }, 7000);
  };

  document.addEventListener('ajaxProduct:added', showToast);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'product-form') showToast();
    });
  }
})();
